// @ts-nocheck
import {
  Box,
  Card,
  FormControl,
  FormHelperText,
  Grid,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { memo, useContext, useEffect, useMemo, useState } from "react";
import parse from "html-react-parser";
import HlxButton from "../../commonComponents/button/button";
import "./deliveryCard.scss";
import GeneralDialog from "component/dialogComponents/generalDialog/generalDialog";
import Textbox from "component/commonComponents/Textbox";
import SelectBox from "component/commonComponents/selectBox";
import { StyledAsterisk } from "component/commonComponents/Textbox/Textbox.style";
import RadioGroupComponent from "component/commonComponents/radioGroup/radioGroupComponent";
import InfoIcon from "@mui/icons-material/Info";
import { ButtonLink } from "./deliveryCard.style";
import { mapTextId } from "../../../utils/mapTextId";
import { ThemeContext } from "styled-components";
import isEmpty from 'lodash/isEmpty';
import { configDefaultAddress, loadChildField, sortCountriesByName } from "./deliveryCard.util";
import { PC_PROCESSING } from "../../../constants/pcProcessing";
import { COUNTRIES } from './addressData';

const ShippingType = {
  PICK_UP: "PickUp",
  DELIVERY: "Delivery",
};

const AddressDialog = ({
  onClose,
  open,
  generateAddress,
  countryUserLogin,
  addressAfterSave,
  locale = {
    updateAddress: "Update Address",
    countryRegion: "Country/Region",
    txtOk: "OK",
    txtCancel: "CANCEL",
  },
  deliverySetting,
  pcProcessing,
  getPickUpDepotList,
  disabledCountry,
  validateRequiredFieldsEmpty,
  detailsPopulated,
  setMissingRequiredInfo,
  setDeliveryAddress,
}) => {

  const pickUpCountry = Array.isArray(deliverySetting) ? deliverySetting.map((item:any) => item.country) : [];

  const theme = useContext(ThemeContext);
  const smUp = useMediaQuery(`(min-width:${theme.breakpoints.sm})`);
  const isLoading = false;
  const [dataJsonConfigAdd, setDataJsonConfigAdd] = useState(generateAddress);

  const { defaultAddress, defaultValidation } = configDefaultAddress(
    dataJsonConfigAdd,
    countryUserLogin,
    addressAfterSave,
    true,
  );
  const [address, setAddress] = useState(defaultAddress);
  const [validationFields, setValidationFields] = useState(defaultValidation);
  const [fieldTargetChange, setFieldTargetChange] = useState({});
  const [shippingType, setShippingType] = useState(
    !!addressAfterSave.isPickUp ? ShippingType.PICK_UP : ShippingType.DELIVERY
    );
  const [isPickupCountry, setIsPickupCountry] = useState(
    pickUpCountry?.includes(defaultAddress?.country)
  );
  const [pickUpDepotList, setPickUpDepotList] = useState([]);
  const [pickUpDepot, setPickUpDepot] = useState(addressAfterSave.pickUpDepot);

  const selectedCountryDeliverySetting = useMemo(() => Array.isArray(deliverySetting) ? deliverySetting.filter((item: any) => item.country === address.country)[0] : {}, [address.country]);
  const checkPickUpSetting = Array.isArray(selectedCountryDeliverySetting?.types) ? selectedCountryDeliverySetting?.types?.includes(pcProcessing) : !isEmpty(selectedCountryDeliverySetting);
  const showPickUpSelection = selectedCountryDeliverySetting?.pickup && checkPickUpSetting;
  const showDeliverySelection = showPickUpSelection && selectedCountryDeliverySetting?.shipping;
  const isPcReturn = pcProcessing === PC_PROCESSING.PC_RETURN || pcProcessing === PC_PROCESSING.PC_RETURN_UNKNOWN;
  const sortedCountryList = useMemo(() => {
    return sortCountriesByName(dataJsonConfigAdd?.countryList, countryUserLogin);
  }, [dataJsonConfigAdd?.countryList, countryUserLogin]);

  const selectedCountryOption = useMemo(() => {
    return sortedCountryList.find(({ code }) => code === address.country) || {};
  }, [sortedCountryList, address.country]);

  useEffect(() => {
    if (!isPickupCountry) return;
    (async () => {
      const depotList = await getPickUpDepotList(address?.country);
      setPickUpDepotList(depotList);
    })();
  }, [address.country, isPickupCountry]);

  const handleChangeSelect = (e) => {
    const value = e.target.value;
    setShippingType(value);
    setAddress((address) => ({
      ...address,
      isPickUp: value === ShippingType.PICK_UP,
    }));
    updateValidation(value);
  };

  const handlePickUpDepotChange = (e, value) => {
    setPickUpDepot(value);
    setAddress((address) => ({
      ...address,
      isPickUp: true,
      pickUpDepot: value,
    }));
    setFieldTargetChange({ pickUpDepot: true });
  };

  // check validatin onChange
  useEffect(() => {
    setValidationFields({
      ...validationFields,
      ...fieldTargetChange,
    });
  }, [fieldTargetChange]);

  const getRequiredFields = (dataField) => {
    const requiredFields = {};

    if (!Array.isArray(dataField) || dataField?.length === 0) {
      return requiredFields;
    };

    dataField.forEach(section => {
      section.controls.forEach(control => {
        const isRequired = control.validation.some(validation => validation.type === "required");
        if (isRequired) {
          requiredFields[control.key] = true;
        }
      });
    });

    return requiredFields;
  };

  useEffect(() => {
    const fields = getRequiredFields(dataJsonConfigAdd?.dataField[address?.country]);
    const hasEmptyRequiredField = validateRequiredFieldsEmpty(fields, address);
    const hasShippingSetting = selectedCountryDeliverySetting?.shipping || selectedCountryDeliverySetting?.shipping === undefined;
    setMissingRequiredInfo(hasEmptyRequiredField);

    if(!hasEmptyRequiredField && detailsPopulated && hasShippingSetting) {
      handleClose(true, true);
    };
  }, [detailsPopulated]);

  useEffect(() => {
    const isPickUpOnly = showPickUpSelection && !selectedCountryDeliverySetting?.shipping;
    if (isPickUpOnly) {
      setDeliveryAddress((prev) => ({ ...prev, isPickUp: true }));
    }
  }, [selectedCountryDeliverySetting]);

  useEffect(() => {
    if (selectedCountryDeliverySetting?.pickup !== selectedCountryDeliverySetting?.shipping && checkPickUpSetting) {
      const shippingType = selectedCountryDeliverySetting?.pickup
        ? ShippingType.PICK_UP
        : ShippingType.DELIVERY;
      setShippingType(shippingType);
      updateValidation(shippingType);
    } else {
      setShippingType(!!address.isPickUp ? ShippingType.PICK_UP : ShippingType.DELIVERY);
    }
  }, [address.country]);

  const updateValidation = (shippingType = '') => {
    if (shippingType === ShippingType.PICK_UP) {
      setValidationFields({ pickUpDepot: true });
    }
    if (shippingType === ShippingType.DELIVERY) {
      const { defaultValidation } = configDefaultAddress(generateAddress, address.country, addressAfterSave);
      setValidationFields(defaultValidation);
    }
  };

  const handleChange = (event, fieldName, parent, requiredField) => {
    let val = "";
    if (event) {
      val = event?.target ? event.target.value : event.code;
    }
    if (fieldName === "country") {
      setIsPickupCountry(pickUpCountry?.includes(val));
      const { defaultAddress, defaultValidation } = configDefaultAddress(
        generateAddress,
        val,
        addressAfterSave
      );
      delete defaultAddress.pickUpDepot;
      setAddress(defaultAddress);
      setValidationFields(defaultValidation);
      setPickUpDepot({});
    } else {
      if (requiredField) {
        setFieldTargetChange({ [fieldName]: !!val });
      }
      setAddress({
        ...address,
        [fieldName]: val,
      });
    }

    // load field child follow parent
    if (!!parent) {
      const { dataFieldCustom, defaultValidation, defaultAddress } =
        loadChildField(dataJsonConfigAdd, val, address, fieldName);
      setAddress(defaultAddress);
      setDataJsonConfigAdd({
        countryList: dataJsonConfigAdd.countryList,
        dataField: {
          ...dataJsonConfigAdd.dataField,
          [address.country]: dataFieldCustom,
        },
      });
      setValidationFields(defaultValidation);
    }
  };

  const validateField = (fieldName) => {
    setValidationFields({
      ...validationFields,
      [fieldName]: !!address[fieldName],
    });
  };

  const handleClose = (isUpdateAddress = false, isDetailsPopulated = false) => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const validations = shippingType === ShippingType.PICK_UP
      ? { pickUpDepot: true }
      : validationFields;

    const { isValid, validFields } = Object.keys(validations).reduce(
      (result, key) => {
        const isFieldValid = !!address[key];
        return {
          isValid: result.isValid && isFieldValid,
          validFields: {
            ...result.validFields,
            [key]: isFieldValid,
          },
        };
      },
      { isValid: true, validFields: {} }
    );

    if (isUpdateAddress) {
      setValidationFields(validFields);
      if (isValid) {
        onClose(address, isUpdateAddress, isDetailsPopulated);
      }
      return;
    }
    // Cancel button flow
    const country = addressAfterSave.country || countryUserLogin;
    const { dataFieldCustom, defaultAddress, defaultValidation } = configDefaultAddress(
      generateAddress,
      country,
      addressAfterSave,
      true
    );
    const isValidPickUp = addressAfterSave?.isPickUp && addressAfterSave?.country;

    if (isValidPickUp) {
      setAddress(addressAfterSave);
      setValidationFields({ pickUpDepot: true });
      setPickUpDepot(addressAfterSave.pickUpDepot);
    } else {
      setAddress(defaultAddress);
      setValidationFields(defaultValidation);
      setIsPickupCountry(pickUpCountry?.includes(defaultAddress?.country));
      setDataJsonConfigAdd(prev => ({
        countryList: generateAddress.countryList,
        dataField: {
          ...generateAddress.dataField,
          [country]: dataFieldCustom,
        },
      }));
    }
    setShippingType(isValidPickUp ? ShippingType.PICK_UP : ShippingType.DELIVERY);
    onClose(address, isUpdateAddress, isDetailsPopulated);
  };
  const showGenerateAddress = (data) => {
    const { dataField } = data;

    let result;
    if (dataField[address.country].length > 0) {
      result = dataField[address.country].map((code) => {
        return code.controls.map((field) => {
          let arrValidation = field?.validation || [];
          let getRequired = arrValidation.find((r) => r.type === "required");
          let requiredField =
            arrValidation.length > 0 && !!getRequired ? true : false;

          const label = locale?.dataField?.label[field.label] || field.label;
          const placeHolder =
            field.placeHolder === "Apartment, suite, etc. (optional)"
              ? locale?.dataField.placeHolder["Apartment, suite, etc"] ||
                field.placeHolder
              : locale?.dataField.placeHolder[field.placeHolder] ||
                field.placeHolder;

          // type textField
          if (field.type === "textField") {
            return (
              <Grid
                container
                xs={field.col?.xs ? +field.col?.xs : 12}
                md={field.col?.md ? +field.col?.md : 12}
                className="mt-20"
                item
                key={field.key}
              >
                {/* <Grid xs={12} className={!!getRequired ? 'form-left-title' : 'form-left-title--no-require'} item>
                  <label className={`${!!validationFields[field.key] ? '' : 'title-validated'}`}> {label} </label>
                </Grid> */}
                <Grid xs={12} item>
                  <Textbox
                    type="text"
                    label={label}
                    boldLabel
                    labelSize="small"
                    placeholder={placeHolder}
                    name={field.key}
                    value={address[field.key]}
                    error={!!getRequired && !validationFields[field.key]}
                    onChange={(e) =>
                      handleChange(e, field.key, "", requiredField)
                    }
                    onBlur={() =>
                      getRequired !== undefined ? validateField(field.key) : {}
                    }
                    fullWidth
                    required={!!getRequired}
                    isSpecialCharacterAllowed={false}
                    textboxToolTip={locale?.textboxToolTip}
                    id={`txt-deliverycard-${mapTextId(field.key)}`}
                  />
                </Grid>
              </Grid>
            );
          }

          const hasSubItem = field.hasOwnProperty("parent") && field.key !== field.parent;
          let options = field.data;

          if (hasSubItem && options.length === 0) {
            const parentData = code.controls.find(control => control.key === field.parent)?.data || [];
            const matchedParent = parentData?.find(item => item.code === address[field.parent]);

            options =  matchedParent?.subList ?? [];
          };

          // type selectBox
          return (
            <Grid
              container
              xs={field.col?.xs ? +field.col?.xs : 12}
              md={field.col?.md ? +field.col?.md : 12}
              className="mt-20"
              item
              key={field.key}
            >
              <Grid xs={12} item>
                <FormControl margin="dense" className="select-control">
                  <SelectBox
                    label={label}
                    boldLabel
                    name={field.key}
                    handleChange={(e, val) =>
                      handleChange(
                        val,
                        field.key,
                        field.hasOwnProperty("parent") ? field.parent : "",
                        requiredField
                      )
                    }
                    options={options}
                    onBlur={() => validateField(field.key)}
                    value={
                      options.find(
                        ({ code }) => code === address[field.key]
                      ) || ""
                    }
                    error={!!getRequired && !validationFields[field.key]}
                    valueKey="code"
                    disableClearable
                    required={!!getRequired}
                    isSpecialCharacterAllowed={false}
                    textboxToolTip={locale?.textboxToolTip}
                    locale={locale}
                    id={`drp-deliverycard-${mapTextId(field.key)}`}
                  />
                </FormControl>
              </Grid>
            </Grid>
          );
        });
      });
    }

    return result;
  };

  if (Object.keys(generateAddress).length === 0) {
    return <></>;
  }

  const buttonSubmit = (
    <>
      <HlxButton
        loading={isLoading}
        onClick={() => handleClose(true)}
        label={locale?.txtOk}
        id="btn-deliverycard-ok"
      />
    </>
  );

  const content = (
    <>
      <Grid container className="form-container">
        <Grid container>
          <Grid xs={12} item>
            <FormControl width="100%" margin="dense" className="select-control">
              <SelectBox
                disabled={disabledCountry}
                name="country"
                label={locale?.countryRegion}
                isSpecialCharacterAllowed={false}
                boldLabel
                value={selectedCountryOption}
                handleChange={(e, val) =>
                  handleChange(val, "country", "", true)
                }
                fullWidth
                options={sortedCountryList}
                variant="outlined"
                required
                valueKey="code"
                disableClearable
                textboxToolTip={locale?.textboxToolTip}
                locale={locale}
                id="drp-deliverycard-country"
              />
            </FormControl>
          </Grid>
        </Grid>
        {showDeliverySelection && (
          <Grid container>
            <Grid item xs={12} md={12} className="shipping-type">
              <Grid item className="label-shipping-type">
                {isPcReturn ? locale?.shippingType?.returnLabel : locale?.shippingType.label}<StyledAsterisk>*</StyledAsterisk>
              </Grid>
              <Grid item xs={12} md={12} className="radio-shipping-type">
                <RadioGroupComponent
                  name="address_type"
                  isRow={true}
                  data={[
                    {
                      value: ShippingType.DELIVERY,
                      name: locale?.shippingType.option.delivery,
                    },
                    {
                      value: ShippingType.PICK_UP,
                      name: isPcReturn ? locale?.shippingType.option.office : locale?.shippingType.option.pickUp,
                    },
                  ]}
                  onChange={(e) => handleChangeSelect(e)}
                  selected={shippingType}
                  id="rad-deliverycard-shippingtype"
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        {showPickUpSelection && shippingType === ShippingType.PICK_UP && (
          <Grid container>
            {selectedCountryDeliverySetting?.note && <Grid item xs={12} md={12} className="note-content">
              <Grid item className="note-tooltip">
                <Tooltip
                  title={""}
                  placement="right-start"
                  className="tooltip-container"
                  children={
                    <div>
                      <InfoIcon fontSize="small" htmlColor="black" />
                    </div>
                  }
                />
              </Grid>
              <Grid item>
                <Grid item>
                  {parse(selectedCountryDeliverySetting?.note || "", {
                    decodeEntities: false,
                  })}
                </Grid>
              </Grid>
            </Grid>}
            <Grid item xs={12} md={12} className="select-pickup-location">
              <SelectBox
                label={isPcReturn ? locale?.shippingType?.returnLocation : locale?.shippingType.pickUpLocation}
                required
                placeholder=""
                disableClearable={true}
                value={pickUpDepot}
                options={pickUpDepotList}
                isSpecialCharacterAllowed={false}
                error={!validationFields.pickUpDepot}
                boldLabel
                handleChange={handlePickUpDepotChange}
                valueKey="name"
                textboxToolTip={locale?.textboxToolTip}
                id="drp-deliverycard-pickuplocation"
              />
            </Grid>
          </Grid>
        )}
        {(!showPickUpSelection ||
          shippingType === ShippingType.DELIVERY) &&
          showGenerateAddress(dataJsonConfigAdd)}
      </Grid>
    </>
  );

  return (
    <GeneralDialog
      title={locale?.updateAddress}
      content={content}
      isOpen={open}
      handleClose={() => handleClose(false)}
      showComment={false}
      buttonSubmit={buttonSubmit}
      deviders={false}
      headerBackgroundDefault={false}
      cancelLabel={locale?.txtCancel}
      commonStyles={smUp ? {} : { padding: '24px 0 0' }}
      classname="delivery-address-dialog delivery-address-root"
    />
  );
};


const DeliveryCard = ({
  title = "Delivery Address:",
  disabled = false,
  dataGenerateAddress = {
    countryList: [...COUNTRIES],
    dataField: {
      usa: []
    }
  },
  countryUserLogin = 'usa',
  onChange = (address) => {
    console.log(address);
  },
  error = false,
  errorText = 'Please update your address',
  addressItem = {},
  locale = {
    addAddress: "Add Address",
    addressType: {
      city: "House number, building or street name, city",
      country: "Country",
      state: "State, zip code",
    },
    pickup: "Pick Up",
    shippingType: {
      label: "Shipping Type",
      option: {
        delivery: "Delivery",
        pickUp: "Pick Up",
      },
      noteText: {
        delivery: "Delivery - The device will reach you in take  4 - 14 days.",
        pickUp: "Pick Up - The device will be ready in 5 days.",
      },
      pickUpLocation: "Pick Up Location",
    },
    countryRegion: "Country/Region",
    editAddress: "Edit Address",
    id: "510f796d-8e25-4ed9-aebf-ca9edf6ae340",
    txtCancel: "CANCEL",
    txtOk: "OK",
    updateAddress: "Update Address",
    dataField: {
      label: {
        "Address Line 1": "Address Line 1",
        "Address Line 2": "Address Line 2",
        "City": "City",
        "State": "State",
        "Zip Code": "Zip Code",
        "Postcode": "Postcode",
        "Suburb": "Suburb",
        "State/territory": "State/territory",
        "Region": "Region",
        "Postal Code": "Postal Code",
        "Province": "Province",
        "Street and House Number": "Street and House Number",
        "City/Town": "City/Town",
        "PIN Code": "PIN Code",
        "Canton": "Canton",
        "Districts": "Districts",
        "Street Name": "Street Name",
        "House Number": "House Number",
        "Town": "Town",
        "Building Information": "Building Information",
        "Sector/Village": "Sector/Village",
        "Address 1": "Address 1",
        "Address 2": "Address 2",
        "Department": "Department",
        "District": "District"
      },
      placeHolder: {
        "Apartment, suite, etc": "Apartment, suite, etc. (optional)",
        "Mail box number or Description of house location": "Mail box number or Description of house location",
        "Optional": "Optional",
        "Floor/Department/District Information": "Floor/Department/District Information",
        "Street name and number": "Street name and number",
        "Nearest intersecting street": "Nearest intersecting street",
        "Apartment Building": "Apartment Building",
        "House number, Street name": "House number, Street name"
      }
    },
    missingRequiredInfo: "Please fill in all required information"
  },
  handleEdit = () => {},
  required = false,
  deliverySetting = [],
  pcProcessing = '',
  getPickUpDepotList = () => Promise.resolve([]),
  disabledCountry = false,
  validateRequiredFieldsEmpty = () => false,
  detailsPopulated = false,
  setShippingAddress = (address) => console.log(address)
}) => {
  let checkAddressItemHasValue = Object.keys(addressItem).length > 0;
  let checkData = Object.keys(dataGenerateAddress).length > 0;
  const [open, setOpen] = React.useState(false);
  const [checkFieldsInit, setCheckFieldsInit] = useState(false);
  const [address, setAddress] = React.useState(
    checkAddressItemHasValue ? addressItem : {}
  );

  const handleClickOpen = () => {
    setOpen(true);
  };
  const [missingRequiredInfo, setMissingRequiredInfo] = useState(false);

  const handleClose = (address, isUpdateAddress = true, isDetailsPopulated = false) => {
    if(!isDetailsPopulated) setOpen(false);
    if (isUpdateAddress) {
      setCheckFieldsInit(true);
      setAddress(address);
      onChange(address);
      setMissingRequiredInfo(false);
    }
  };

  const mapObjAddress = (address, jsonConfig) => {
    let objAddressTranslate = {};
    let getSubList = [];
    let arrSelect = [];

    if (!!address?.country) {
      jsonConfig.dataField[address?.country].forEach((code) => {
        code.controls.forEach((field) => {
          // type selectBox
          if (field.type === "comboBox") {
            if (field.hasOwnProperty("parent") && field.key === field.parent) {
              getSubList =
                field.data.find((item) => item.code === address[field.parent])
                  ?.subList || [];
            }

            arrSelect.push({
              key: field.key,
              dataSelect:
                field.hasOwnProperty("parent") && field.key !== field.parent
                  ? getSubList
                  : field.data,
            });
          }
        });
      });
    }

    if (arrSelect.length > 0) {
      Object.keys(address).forEach((ele) => {
        const itemSelect = arrSelect.find((item) => item.key === ele);
        if (!!itemSelect) {
          let getName = itemSelect.dataSelect.find(
            (v) => v.code === address[itemSelect.key]
          )?.name;
          objAddressTranslate = {
            ...address,
            ...objAddressTranslate,
            [itemSelect.key]: getName,
          };
        }
      });
    } else {
      objAddressTranslate = {
        ...address,
        ...objAddressTranslate,
      };
    }
    return objAddressTranslate;
  };

  const convertValueFromCodeToName = (address, field, dataField) => {
    const newAddress = mapObjAddress(
      {
        country: address?.country?.toLowerCase() || "",
        [dataField]: address?.[field]?.toLowerCase() || "",
      },
      dataGenerateAddress
    );
    return newAddress[dataField] || address[field] || '';
  };

  let addressMapped = mapObjAddress(address, dataGenerateAddress);

  const arrKey = Object.keys(address);
  let listKey = "";
  let result = "";
  let countryData = checkData
    ? dataGenerateAddress.countryList.find((c) => c.code === address.country)
    : {};
  let getCountryName = `<p>${countryData?.name}</p>`;

  arrKey.forEach((key, index) => {
    listKey += `${key}${index === arrKey.length - 1 ? "" : "|"}`;
  });

  let regCustom = new RegExp(`\\b(?:${listKey})\\b`, "gi");

  let replaceOutputData = countryData?.outputData
    ? countryData.outputData.replace(
        regCustom,
        (matched) => addressMapped[matched] || ""
      )
    : "";
  if (address.isPickUp) {
    const pickUpLocation = address.pickUpDepot?.location || {};
    const hasData = Object.keys(pickUpLocation).length > 0;

    if (hasData) {
      const { name, street, city, zip } = pickUpLocation;
      const state = convertValueFromCodeToName(pickUpLocation, "state", "states");
      replaceOutputData = `<p>${name}</p><p>${street}</p><p>${city}, ${state}, ${zip}</p>`;
    } else {
      replaceOutputData = '';
    }
  }
  result = replaceOutputData + getCountryName;
  setShippingAddress(result);

  return (
    <div className="uptime-delivery-card">
      <h5 className="heading5">
        {address.isPickUp ? locale?.pickup : title}
        {required && <StyledAsterisk>*</StyledAsterisk>}
      </h5>
      <Card className="card-wrap">
        <FormControl error={error || missingRequiredInfo} className="uptime-delivery-card-body">
          <Grid container>
            <Grid item xs={8}>
              {checkAddressItemHasValue || checkFieldsInit ? (
                <Box className="content-box bodyRegular">
                  {parse(result, { decodeEntities: false })}
                </Box>
              ) : (
                <Box className="content-box bodyRegular">
                  <p>{locale?.addressType.city}</p>
                  <p>{locale?.addressType.state}</p>
                  <p>{locale?.addressType.country}</p>
                </Box>
              )}
            </Grid>

            <Grid item xs={4}>
              <ButtonLink
                className="delivery-btn-link buttonLink"
                onClick={!disabled ? handleClickOpen : handleEdit}
                id={'btn-deliverycard-update'}
              >
                {checkAddressItemHasValue || checkFieldsInit
                  ? locale?.editAddress
                  : locale?.addAddress}
              </ButtonLink>
            </Grid>
          </Grid>

          {(error || missingRequiredInfo) && (
            <FormHelperText id="my-helper-text" sx={{ marginLeft: '0 !important' }}>{errorText || locale?.missingRequiredInfo}</FormHelperText>
          )}
        </FormControl>
      </Card>

      <AddressDialog
        open={open}
        onClose={handleClose}
        generateAddress={dataGenerateAddress}
        addressAfterSave={address}
        countryUserLogin={countryUserLogin?.toLowerCase()}
        locale={locale}
        deliverySetting={deliverySetting}
        pcProcessing={pcProcessing}
        getPickUpDepotList={getPickUpDepotList}
        disabledCountry={disabledCountry}
        validateRequiredFieldsEmpty={validateRequiredFieldsEmpty}
        detailsPopulated={detailsPopulated}
        setMissingRequiredInfo={setMissingRequiredInfo}
        setDeliveryAddress={setAddress}
      />
    </div>
  );
};

DeliveryCard.propTypes = {
  title: PropTypes.string,
  addressItem: PropTypes.object,
  defaultCountry: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.bool,
  errorText: PropTypes.string,
  disabled: PropTypes.bool,
  locale: PropTypes.object,
  handleEdit: PropTypes.func,
  required: PropTypes.bool,
  deliverySetting: PropTypes.array,
  pcProcessing: PropTypes.string,
  getPickUpDepotList: PropTypes.func,
  disabledCountry: PropTypes.bool,
  validateRequiredFieldsEmpty: PropTypes.func,
  detailsPopulated: PropTypes.bool,
  setShippingAddress: PropTypes.func,
  dataGenerateAddress: PropTypes.object,
  countryUserLogin: PropTypes.string
};

export default memo(DeliveryCard);
